import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';

import SelectMenu from "./crm/SelectMenu";
const UserManagement = () => {
  const functions = getFunctions();

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, totalCount: 0, totalPages: 1 });
  const [dashboardStats, setDashboardStats] = useState(null);
  const [activeDetailTab, setActiveDetailTab] = useState('info');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionAction, setSubscriptionAction] = useState('extend');
  const [subscriptionDays, setSubscriptionDays] = useState(30);
  const [subscriptionCategory, setSubscriptionCategory] = useState('Lawyer-Basic');
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'search'
  const [allUsers, setAllUsers] = useState([]);
  const [allUsersPagination, setAllUsersPagination] = useState({ page: 1, pageSize: 50, totalCount: 0, totalPages: 1 });
  const [isLoadingAllUsers, setIsLoadingAllUsers] = useState(false);
  const [selectedUserType, setSelectedUserType] = useState('lawyers'); // 'lawyers' or 'client'

  // Fetch dashboard stats and all users on mount
  useEffect(() => {
    fetchDashboardStats();
    fetchAllUsers(1);
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const getStats = httpsCallable(functions, 'adminGetDashboardStats');
      const result = await getStats();
      setDashboardStats(result.data.stats);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  const fetchAllUsers = async (page = 1, userType = selectedUserType) => {
    setIsLoadingAllUsers(true);
    try {
      const searchFn = httpsCallable(functions, 'adminSearchUsers');
      const result = await searchFn({
        query: '',
        page,
        pageSize: 50,
        userType: userType,
      });
      setAllUsers(result.data.users);
      setAllUsersPagination(result.data.pagination);
    } catch (error) {
      console.error('Error fetching all users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setIsLoadingAllUsers(false);
    }
  };

  const handleUserTypeChange = (userType) => {
    setSelectedUserType(userType);
    fetchAllUsers(1, userType);
  };

  const searchUsers = async (page = 1) => {
    setIsLoading(true);
    try {
      const searchFn = httpsCallable(functions, 'adminSearchUsers');
      const result = await searchFn({
        query: searchQuery,
        page,
        pageSize: pagination.pageSize,
        userType: 'lawyers',
      });
      setUsers(result.data.users);
      setPagination(result.data.pagination);
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Failed to search users');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserDetails = async (userId, userType = selectedUserType) => {
    setIsLoadingDetails(true);
    setSelectedUser(userId);
    setActiveDetailTab('info');
    setShowUserDetailsModal(true);
    try {
      const getDetails = httpsCallable(functions, 'adminGetUserDetails');
      const result = await getDetails({ userId, userType });
      setUserDetails(result.data);
    } catch (error) {
      console.error('Error fetching user details:', error);
      toast.error('Failed to fetch user details');
      setShowUserDetailsModal(false);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const closeUserDetailsModal = () => {
    setShowUserDetailsModal(false);
    setSelectedUser(null);
    setUserDetails(null);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser || Object.keys(editFormData).length === 0) return;

    setIsLoading(true);
    try {
      const updateFn = httpsCallable(functions, 'adminUpdateUser');
      await updateFn({
        userId: selectedUser,
        userType: selectedUserType,
        updates: editFormData,
      });
      toast.success('User updated successfully');
      setShowEditModal(false);
      fetchUserDetails(selectedUser);
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateSubscription = async () => {
    if (!selectedUser) return;

    setIsLoading(true);
    try {
      const updateFn = httpsCallable(functions, 'adminUpdateSubscription');
      await updateFn({
        userId: selectedUser,
        action: subscriptionAction,
        data: {
          days: subscriptionDays,
          category: subscriptionCategory,
        },
      });
      toast.success('Subscription updated successfully');
      setShowSubscriptionModal(false);
      fetchUserDetails(selectedUser);
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast.error('Failed to update subscription');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCase = async (caseId, caseType) => {
    if (!window.confirm('Are you sure you want to delete this case?')) return;

    setIsLoading(true);
    try {
      const deleteFn = httpsCallable(functions, 'adminDeleteCase');
      await deleteFn({ caseId, caseType });
      toast.success('Case deleted successfully');
      fetchUserDetails(selectedUser);
    } catch (error) {
      console.error('Error deleting case:', error);
      toast.error('Failed to delete case');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async (deleteAuth = false) => {
    setIsLoading(true);
    try {
      const deleteFn = httpsCallable(functions, 'adminDeleteUser');
      const result = await deleteFn({
        userId: selectedUser,
        userType: selectedUserType,
        deleteAuth,
      });
      toast.success('User deleted successfully');
      setShowDeleteConfirm(false);
      setShowUserDetailsModal(false);
      setSelectedUser(null);
      setUserDetails(null);
      fetchAllUsers(allUsersPagination.page);
      fetchDashboardStats();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';

    let d;

    // Handle Firestore Timestamp objects
    if (date && typeof date === 'object') {
      if (date.toDate && typeof date.toDate === 'function') {
        // Firestore Timestamp with toDate method
        d = date.toDate();
      } else if (date.seconds !== undefined) {
        // Firestore Timestamp with seconds property
        d = new Date(date.seconds * 1000);
      } else if (date._seconds !== undefined) {
        // Firestore Timestamp serialized format
        d = new Date(date._seconds * 1000);
      } else {
        d = new Date(date);
      }
    } else if (typeof date === 'string' || typeof date === 'number') {
      d = new Date(date);
    } else {
      return 'N/A';
    }

    // Check if date is valid
    if (isNaN(d.getTime())) return 'N/A';

    return d.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getSubscriptionBadge = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'free':
        return 'bg-blue-100 text-blue-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Dashboard Stats */}
      {dashboardStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-600 font-medium">Total Lawyers</p>
            <p className="text-2xl font-bold text-blue-800">{dashboardStats.users.totalLawyers}</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <p className="text-sm text-purple-600 font-medium">Total Clients</p>
            <p className="text-2xl font-bold text-purple-800">{dashboardStats.users.totalClients}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-600 font-medium">Active Subscriptions</p>
            <p className="text-2xl font-bold text-green-800">{dashboardStats.subscriptions.active}</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-600 font-medium">Paid Plans</p>
            <p className="text-2xl font-bold text-yellow-800">{dashboardStats.subscriptions.paid}</p>
          </div>
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <p className="text-sm text-indigo-600 font-medium">Total Cases</p>
            <p className="text-2xl font-bold text-indigo-800">{dashboardStats.cases.total}</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-600 font-medium">Expired Subs</p>
            <p className="text-2xl font-bold text-red-800">{dashboardStats.subscriptions.expired}</p>
          </div>
        </div>
      )}

      {/* View Mode Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex">
            <button
              onClick={() => setViewMode('all')}
              className={`px-6 py-4 text-sm font-medium transition-colors ${
                viewMode === 'all'
                  ? 'border-b-2 border-indigo-600 text-indigo-600 bg-indigo-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              All Users
            </button>
            <button
              onClick={() => setViewMode('search')}
              className={`px-6 py-4 text-sm font-medium transition-colors ${
                viewMode === 'search'
                  ? 'border-b-2 border-indigo-600 text-indigo-600 bg-indigo-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              Search Users
            </button>
          </nav>
        </div>

        {/* All Users View */}
        {viewMode === 'all' && (
          <div className="p-6">
            {/* User Type Filter */}
            <div className="flex items-center gap-4 mb-6">
              <span className="text-sm font-medium text-gray-700">Filter by type:</span>
              <div className="flex gap-2">
                <button
                  onClick={() => handleUserTypeChange('lawyers')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedUserType === 'lawyers'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Lawyers
                </button>
                <button
                  onClick={() => handleUserTypeChange('client')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedUserType === 'client'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Clients
                </button>
              </div>
              <span className="ml-auto text-sm text-gray-500">
                Total: {allUsersPagination.totalCount} users
              </span>
            </div>

            {/* Users Table */}
            {isLoadingAllUsers ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          User
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Email
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Phone
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Category
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Location
                        </th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Created
                        </th>
                        <th scope="col" className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {allUsers.map((user) => (
                        <tr
                          key={user.id}
                          onClick={() => fetchUserDetails(user.id)}
                          className={`cursor-pointer transition-colors hover:bg-gray-50 ${
                            selectedUser === user.id ? 'bg-indigo-50' : ''
                          }`}
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                selectedUserType === 'lawyers' ? 'bg-indigo-100' : 'bg-purple-100'
                              }`}>
                                <span className={`text-sm font-semibold ${
                                  selectedUserType === 'lawyers' ? 'text-indigo-600' : 'text-purple-600'
                                }`}>
                                  {(user.name || 'U')[0].toUpperCase()}
                                </span>
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900 truncate max-w-[150px]">{user.name || 'Unknown'}</p>
                                <p className="text-xs text-gray-400 truncate max-w-[150px]">{user.id}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <p className="text-sm text-gray-700 truncate max-w-[200px]">{user.email || 'N/A'}</p>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <p className="text-sm text-gray-700">{user.mobileNumber || user.mobileWithCountryCode || 'N/A'}</p>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              user.userCategory || user.userCateogry
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}>
                              {user.userCategory || user.userCateogry || 'No Category'}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <p className="text-sm text-gray-700 truncate max-w-[120px]">{user.location || user.state || 'N/A'}</p>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <p className="text-sm text-gray-500">
                              {user.createdAt ? formatDate(user.createdAt) : 'N/A'}
                            </p>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-center">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                fetchUserDetails(user.id);
                              }}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {allUsers.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p>No users found</p>
                  </div>
                )}

                {/* All Users Pagination */}
                {allUsersPagination.totalPages > 1 && (
                  <div className="mt-4 flex justify-between items-center">
                    <p className="text-sm text-gray-500">
                      Showing {((allUsersPagination.page - 1) * allUsersPagination.pageSize) + 1} to {Math.min(allUsersPagination.page * allUsersPagination.pageSize, allUsersPagination.totalCount)} of {allUsersPagination.totalCount} users
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => fetchAllUsers(allUsersPagination.page - 1)}
                        disabled={allUsersPagination.page <= 1 || isLoadingAllUsers}
                        className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50 flex items-center gap-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Previous
                      </button>
                      <span className="px-3 py-1.5 text-sm bg-indigo-50 text-indigo-600 rounded-lg font-medium">
                        {allUsersPagination.page}
                      </span>
                      <span className="text-sm text-gray-500">of {allUsersPagination.totalPages}</span>
                      <button
                        onClick={() => fetchAllUsers(allUsersPagination.page + 1)}
                        disabled={allUsersPagination.page >= allUsersPagination.totalPages || isLoadingAllUsers}
                        className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50 flex items-center gap-1"
                      >
                        Next
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Search View */}
        {viewMode === 'search' && (
          <div className="p-6">
            <div className="flex gap-4 mb-6">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search by name, email, phone, or user ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchUsers(1)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={() => searchUsers(1)}
                disabled={isLoading}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50"
              >
                {isLoading ? 'Searching...' : 'Search'}
              </button>
            </div>

            {/* Search Results */}
            <div className="max-h-[400px] overflow-y-auto border rounded-lg">
              {users.length > 0 ? (
                users.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => fetchUserDetails(user.id)}
                    className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedUser === user.id ? 'bg-indigo-50 border-l-4 border-l-indigo-600' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                        <span className="text-indigo-600 font-semibold">
                          {(user.name || 'U')[0].toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{user.name || 'Unknown'}</p>
                        <p className="text-sm text-gray-500 truncate">{user.email}</p>
                        <p className="text-xs text-gray-400">{user.userCategory || user.userCateogry || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <p>Enter a search term and click Search to find users.</p>
                </div>
              )}
            </div>

            {/* Search Pagination */}
            {pagination.totalPages > 1 && (
              <div className="mt-4 flex justify-center items-center gap-4">
                <button
                  onClick={() => searchUsers(pagination.page - 1)}
                  disabled={pagination.page <= 1 || isLoading}
                  className="px-4 py-2 text-sm border rounded-lg disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => searchUsers(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages || isLoading}
                  className="px-4 py-2 text-sm border rounded-lg disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* User Details Modal */}
      {showUserDetailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {isLoadingDetails ? (
              <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
              </div>
            ) : userDetails ? (
              <>
                {/* User Header */}
                <div className="p-6 bg-gradient-to-r from-indigo-600 to-purple-600 text-white relative">
                  <button
                    onClick={closeUserDetailsModal}
                    className="absolute top-4 right-4 text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-2xl font-bold">
                      {(userDetails.user.name || 'U')[0].toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="text-2xl font-bold">{userDetails.user.name || 'Unknown User'}</h3>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSubscriptionBadge(userDetails.subscription.status)}`}>
                          {userDetails.subscription.status.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-indigo-100 mt-1">{userDetails.user.email}</p>
                      <p className="text-indigo-200 text-sm mt-1">ID: {userDetails.user.id}</p>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-4 text-sm">
                    <div className="bg-white/10 px-3 py-1 rounded-full">
                      <span className="text-indigo-200">Category:</span>{' '}
                      <span className="font-medium">{userDetails.user.userCategory || userDetails.user.userCateogry || 'N/A'}</span>
                    </div>
                    <div className="bg-white/10 px-3 py-1 rounded-full">
                      <span className="text-indigo-200">Phone:</span>{' '}
                      <span className="font-medium">{userDetails.user.mobileNumber || 'N/A'}</span>
                    </div>
                    <div className="bg-white/10 px-3 py-1 rounded-full">
                      <span className="text-indigo-200">Cases:</span>{' '}
                      <span className="font-medium">{userDetails.cases.totalCount}</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="p-4 bg-gray-50 border-b flex gap-2 flex-wrap">
                  <button
                    onClick={() => {
                      setEditFormData({
                        name: userDetails.user.name || '',
                        email: userDetails.user.email || '',
                        mobileNumber: userDetails.user.mobileNumber || '',
                        userCategory: userDetails.user.userCategory || userDetails.user.userCateogry || '',
                      });
                      setShowEditModal(true);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit User
                  </button>
                  <button
                    onClick={() => setShowSubscriptionModal(true)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Manage Subscription
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete User
                  </button>
                </div>

                {/* Detail Tabs */}
                <div className="border-b border-gray-200 bg-white">
                  <nav className="flex overflow-x-auto">
                    {[
                      { id: 'info', label: 'Basic Info', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
                      { id: 'subscription', label: 'Subscription', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
                      { id: 'cases', label: `Cases (${userDetails.cases.totalCount})`, icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
                      { id: 'files', label: `Files (${userDetails.files.totalCount})`, icon: 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z' },
                      { id: 'notifications', label: 'Notifications', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveDetailTab(tab.id)}
                        className={`px-4 py-3 text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
                          activeDetailTab === tab.id
                            ? 'border-b-2 border-indigo-600 text-indigo-600 bg-indigo-50'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                        </svg>
                        {tab.label}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="p-6 max-h-[400px] overflow-y-auto bg-gray-50">
                  {/* Basic Info Tab */}
                  {activeDetailTab === 'info' && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {Object.entries(userDetails.user).map(([key, value]) => {
                        if (key === 'fcmTokens' || key === 'id') return null;
                        return (
                          <div key={key} className="bg-white rounded-lg p-3 shadow-sm">
                            <p className="text-xs text-gray-500 uppercase font-medium">{key}</p>
                            <p className="font-medium text-gray-900 mt-1 break-words">
                              {typeof value === 'object' ? JSON.stringify(value) : String(value || 'N/A')}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Subscription Tab */}
                  {activeDetailTab === 'subscription' && (
                    <div className="space-y-6">
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <h5 className="font-semibold mb-3">Current Status</h5>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <p className="text-xs text-gray-500">Status</p>
                            <p className={`font-bold text-lg ${
                              userDetails.subscription.status === 'paid' ? 'text-green-600' :
                              userDetails.subscription.status === 'free' ? 'text-blue-600' : 'text-red-600'
                            }`}>
                              {userDetails.subscription.status.toUpperCase()}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Case Limit</p>
                            <p className="font-bold text-lg">{userDetails.subscription.caseLimit === Infinity ? 'Unlimited' : userDetails.subscription.caseLimit}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Always Unlocked</p>
                            <p className="font-bold text-lg">{userDetails.subscription.alwaysUnlocked}</p>
                          </div>
                        </div>
                      </div>

                      {userDetails.subscription.active && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <h5 className="font-semibold mb-2 text-green-800">Active Subscription</h5>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <p className="text-gray-500">Type</p>
                              <p className="font-medium">{userDetails.subscription.active.type}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Category</p>
                              <p className="font-medium">{userDetails.subscription.active.category || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">End Date</p>
                              <p className="font-medium">{formatDate(userDetails.subscription.active['end-time'])}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <h5 className="font-semibold mb-3">Transaction History</h5>
                        {userDetails.subscription.history.length > 0 ? (
                          <div className="space-y-2">
                            {userDetails.subscription.history.map((tx, idx) => (
                              <div key={idx} className="border rounded-lg p-3 text-sm bg-gray-50">
                                <div className="flex justify-between">
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    tx.type === 'paid' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                                  }`}>
                                    {tx.type.toUpperCase()} {tx.category && `- ${tx.category}`}
                                  </span>
                                  {tx.extendedByAdmin && <span className="text-orange-600 text-xs">Extended by Admin</span>}
                                </div>
                                <div className="mt-2 flex gap-4 text-gray-600">
                                  <span>Start: {formatDate(tx['start-time'])}</span>
                                  <span>End: {formatDate(tx['end-time'])}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-500">No subscription history</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Cases Tab */}
                  {activeDetailTab === 'cases' && (
                    <div className="space-y-6">
                      <div className="flex gap-4 mb-4">
                        <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm">
                          <span className="font-semibold">{userDetails.cases.pending.length}</span> Pending
                        </span>
                        <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm">
                          <span className="font-semibold">{userDetails.cases.disposed.length}</span> Disposed
                        </span>
                        <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">
                          <span className="font-semibold">{userDetails.cases.lockedCount}</span> Locked
                        </span>
                      </div>

                      {userDetails.cases.pending.length > 0 && (
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                          <h5 className="font-semibold mb-3">Pending Cases</h5>
                          <div className="space-y-2">
                            {userDetails.cases.pending.map((c) => (
                              <div key={c.id} className="border rounded-lg p-3 text-sm flex justify-between items-start bg-gray-50">
                                <div>
                                  <p className="font-medium">{c.caseNo}</p>
                                  <p className="text-gray-600">{c.caseType}</p>
                                  <p className="text-gray-500 text-xs">{c.petitionerName} vs {c.respondentName}</p>
                                  <p className="text-gray-500 text-xs">Next Hearing: {c.nextHearingDate || 'Not set'}</p>
                                </div>
                                <button
                                  onClick={() => handleDeleteCase(c.id, 'pending')}
                                  className="text-red-600 hover:text-red-800 text-xs hover:bg-red-50 px-2 py-1 rounded"
                                >
                                  Delete
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {userDetails.cases.disposed.length > 0 && (
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                          <h5 className="font-semibold mb-3">Disposed Cases</h5>
                          <div className="space-y-2">
                            {userDetails.cases.disposed.map((c) => (
                              <div key={c.id} className="border rounded-lg p-3 text-sm flex justify-between items-start bg-gray-50">
                                <div>
                                  <p className="font-medium">{c.caseNo}</p>
                                  <p className="text-gray-600">{c.caseType}</p>
                                  <p className="text-gray-500 text-xs">{c.petitionerName} vs {c.respondentName}</p>
                                </div>
                                <button
                                  onClick={() => handleDeleteCase(c.id, 'disposed')}
                                  className="text-red-600 hover:text-red-800 text-xs hover:bg-red-50 px-2 py-1 rounded"
                                >
                                  Delete
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {userDetails.cases.pending.length === 0 && userDetails.cases.disposed.length === 0 && (
                        <p className="text-gray-500 text-center py-8">No cases found</p>
                      )}
                    </div>
                  )}

                  {/* Files Tab */}
                  {activeDetailTab === 'files' && (
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      {userDetails.files.items.length > 0 ? (
                        <div className="space-y-2">
                          {userDetails.files.items.map((file) => (
                            <div key={file.id} className="border rounded-lg p-3 text-sm flex justify-between items-center bg-gray-50">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                  </svg>
                                </div>
                                <div>
                                  <p className="font-medium">{file.fileName}</p>
                                  <p className="text-gray-500 text-xs">{file.fileType} - {formatDate(file.uploadedAt)}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-8">No files found</p>
                      )}
                    </div>
                  )}

                  {/* Notifications Tab */}
                  {activeDetailTab === 'notifications' && (
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <p className="text-sm text-gray-500 mb-4">
                        <span className="font-semibold text-indigo-600">{userDetails.notifications.unreadCount}</span> unread notifications
                      </p>
                      {userDetails.notifications.items.length > 0 ? (
                        <div className="space-y-2">
                          {userDetails.notifications.items.map((notif) => (
                            <div key={notif.id} className={`border rounded-lg p-3 text-sm ${!notif.read ? 'bg-blue-50 border-blue-200' : 'bg-gray-50'}`}>
                              <div className="flex justify-between">
                                <p className="font-medium">{notif.title}</p>
                                <span className="text-xs text-gray-400">{formatDate(notif.timestamp)}</span>
                              </div>
                              <p className="text-gray-600 text-xs mt-1">{notif.body}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-8">No notifications</p>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Edit User</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={editFormData.name || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editFormData.email || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                <input
                  type="tel"
                  value={editFormData.mobileNumber || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, mobileNumber: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User Category</label>
                <SelectMenu
                  value={editFormData.userCategory || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, userCategory: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Select Category</option>
                  <option value="Lawyer">Lawyer</option>
                  <option value="Advocate">Advocate</option>
                  <option value="Law student">Law Student</option>
                </SelectMenu>
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 px-4 py-2 border rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateUser}
                disabled={isLoading}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg disabled:opacity-50"
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Manage Subscription</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
                <SelectMenu
                  value={subscriptionAction}
                  onChange={(e) => setSubscriptionAction(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="extend">Extend Current Subscription</option>
                  <option value="create_free">Create Free Subscription</option>
                  <option value="create_paid">Create Paid Subscription</option>
                  <option value="expire">Expire Subscription Now</option>
                  <option value="delete_all">Delete All Subscription Data</option>
                </SelectMenu>
              </div>

              {(subscriptionAction === 'extend' || subscriptionAction === 'create_free' || subscriptionAction === 'create_paid') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Days</label>
                  <input
                    type="number"
                    value={subscriptionDays}
                    onChange={(e) => setSubscriptionDays(parseInt(e.target.value) || 30)}
                    className="w-full px-3 py-2 border rounded-lg"
                    min="1"
                  />
                </div>
              )}

              {subscriptionAction === 'create_paid' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan Category</label>
                  <SelectMenu
                    value={subscriptionCategory}
                    onChange={(e) => setSubscriptionCategory(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="Lawyer-Basic">Lawyer Basic</option>
                    <option value="Lawyer-Premium">Lawyer Premium</option>
                    <option value="Lawyer-Enterprise">Lawyer Enterprise</option>
                    <option value="lawStudent">Law Student</option>
                  </SelectMenu>
                </div>
              )}
            </div>
            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setShowSubscriptionModal(false)}
                className="flex-1 px-4 py-2 border rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateSubscription}
                disabled={isLoading}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50"
              >
                {isLoading ? 'Processing...' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Delete User</h3>
              <p className="text-gray-600 mt-2">
                This will permanently delete the user and all their data including:
              </p>
              <ul className="text-sm text-gray-500 mt-2 text-left list-disc list-inside">
                <li>User profile</li>
                <li>All cases (pending & disposed)</li>
                <li>Subscription data</li>
                <li>Files and notifications</li>
              </ul>
            </div>
            <div className="space-y-2">
              <button
                onClick={() => handleDeleteUser(false)}
                disabled={isLoading}
                className="w-full px-4 py-2 bg-red-600 text-white rounded-lg disabled:opacity-50"
              >
                {isLoading ? 'Deleting...' : 'Delete User Data Only'}
              </button>
              <button
                onClick={() => handleDeleteUser(true)}
                disabled={isLoading}
                className="w-full px-4 py-2 bg-red-800 text-white rounded-lg disabled:opacity-50"
              >
                {isLoading ? 'Deleting...' : 'Delete User + Firebase Auth'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="w-full px-4 py-2 border rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
